import { H4 } from "@/components/ui/typography";
import { MISSING_ID_IN_ROUTE, ROLE, STATUS } from "@/utils/constant";
import { useParams } from "react-router-dom";
import { MemoActiveForm } from "./document-generate-hpdb-section/MemoActiveForm";
import { CreateDataGPONForm } from "./document-generate-hpdb-section/CreateDataGPONForm";
import { CreateHomepassForm } from "./document-generate-hpdb-section/CreateHomepassForm";
import { DownloadHPDBForm } from "./document-generate-hpdb-section/DownloadHPDBForm";
import { CreateMemoHandler } from "./document-generate-hpdb-section/CreateMemoHandler";
import { CreateDataGPONHandler } from "./document-generate-hpdb-section/CreateDataGPONHandler";
import { CreateFibernodeHandler } from "./document-generate-hpdb-section/CreateFibernodeHandler";
import { CreateHomepassHandler } from "./document-generate-hpdb-section/CreateHomepassHandler";
import { GenerateHPDBHandler } from "./document-generate-hpdb-section/GenerateHPDBHandler";
import { GenerateMemoActiveHandler } from "./document-generate-hpdb-section/GenerateMemoActiveHandler";
import { useAuth } from "@/features/auth/useAuth";
import { FormFooter } from "@/components/form/form-footer";
import { DefaultButton, SubmitButton } from "@/components/ui/button";
import { swalError, swalSuccess, swalWithInputText } from "@/utils/lib/swal";
import { useRFS, useSubmitProcessHPDB } from "@/api/rfs";
import axios, { AxiosError } from "axios";
import { UpdateRFSSubmissionForm } from "./document-generate-hpdb-section/UpdateRFSSubmissionForm";
import { useState } from "react";
import { CheckSquare2 } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { GenerateKMZHandler } from "./document-generate-hpdb-section/GenerateKMZHandler";
import { GeneratePDFHandler } from "./document-generate-hpdb-section/GeneratePDFHandler";
import { hasCommonElement } from "@/utils/helper/hasCommonElement";

export function DocumentGenerateHPDBForm() {
  const { id } = useParams();
  if (!id) {
    throw new Error(MISSING_ID_IN_ROUTE);
  }

  const { data: rfs } = useRFS({ id });

  const auth = useAuth();
  const { roles } = auth.getUser();

  const isAdmin = roles.includes(ROLE.ADMINISTRATOR);
  const isValidator = roles.includes(ROLE.VALIDATOR_DOCUMENT_OFFICER);
  const canValidatorSubmit =
    roles.includes(ROLE.VALIDATOR_DOCUMENT_OFFICER) &&
    !(
      rfs?.statusRfsSubmissionLms?.value ===
        STATUS.LMS_SUBMISSION.VALUE.APPROVED_BY_TENANT ||
      rfs?.statusRfsSubmissionLms?.value ===
        STATUS.LMS_SUBMISSION.VALUE.REJECTED_BY_TENANT
    );

  const readOnly = hasCommonElement(roles, [
    ROLE.VENDOR,
    ROLE.MONITORING_TEAM,
    ROLE.VENDOR_VIEW,
  ]);
  const disableFormValidator = isValidator && !canValidatorSubmit;

  const { mutate } = useSubmitProcessHPDB();

  const [memoStatus, setMemoStatus] = useState<string | null>(null);
  const [fibernodeStatus, setFibernodeStatus] = useState<string | null>(null);
  const [gponStatus, setGponStatus] = useState<string | null>(null);
  const [homepassStatus, setHomepassStatus] = useState<string | null>(null);
  const allSuccess = [
    memoStatus,
    fibernodeStatus,
    gponStatus,
    homepassStatus,
  ].every((status) => status === "success" || status === "fdt_exist");

  const [hasMemoData, setHasMemoData] = useState(false);
  const [hasGPONData, setHasGPONData] = useState(false);
  const [hasHomepassData, setHasHomepassData] = useState(false);
  const [hasHPDBData, setHasHPDBData] = useState(false);
  const allFormsHaveData = [
    hasMemoData,
    hasGPONData,
    hasHomepassData,
    hasHPDBData,
  ].every(Boolean);

  const [expanded, setExpanded] = useState<string[]>([]);

  function handleExpandAll() {
    setExpanded(["item-1", "item-2"]);
  }

  function handleCollapseAll() {
    setExpanded([]);
  }

  function handleToggleAccordion(values: string[]) {
    setExpanded(values);
  }

  function handleReject() {
    swalWithInputText.fire({
      title: "Rejected data HPDB",
      text: "Please write reason rejected HPDB",
      icon: "error",
      confirmButtonText: "Submit",
      showLoaderOnConfirm: true,
      showCancelButton: true,
      allowOutsideClick: false,
      input: "textarea",
      inputAttributes: {
        style: "height: 15.625em;",
      },
      inputPlaceholder: "Reason",
      inputValidator: async (value) => {
        if (!value?.trim()) {
          return "Reason is required";
        }
        return null;
      },
      preConfirm: (notes) => {
        if (id) {
          mutate(
            {
              id,
              params: {
                decision: "no",
                withVariable: "true",
              },
              data: {
                notes,
              },
            },
            {
              onSuccess: () => {
                swalSuccess.fire({
                  title: `Success to reject HPDB`,
                });
              },
              onError: (error) => {
                const errorMessage =
                  axios.isAxiosError(error) && error.response
                    ? error.response.data.data
                    : "An unknown error occurred";

                swalError.fire({
                  title: "Failed to reject process HPDB",
                  text: errorMessage,
                });
              },
            },
          );
        } else {
          swalError.fire({
            text: "ID is undefined",
          });
        }
      },
    });
  }

  function handleFinish() {
    if (typeof id === "string") {
      mutate(
        {
          id,
          params: {
            decision: "yes",
            withVariable: "true",
          },
          data: {
            notes: "",
          },
        },
        {
          onSuccess: () => {
            swalSuccess.fire({
              title: `Success to finish process HPDB`,
            });
          },
          onError: (error: Error | AxiosError) => {
            const errorMessage =
              axios.isAxiosError(error) && error.response
                ? error.response.data.data
                : "An unknown error occurred";

            swalError.fire({
              title: "Failed to finish process HPDB",
              text: errorMessage,
            });
          },
        },
      );
    } else {
      console.error("ID is undefined");
    }
  }

  return (
    <>
      <div className="flex justify-end">
        {["item-1", "item-2"].length === expanded.length ? (
          <DefaultButton mood="axiata-blue-outline" onClick={handleCollapseAll}>
            Collapse All
          </DefaultButton>
        ) : (
          <DefaultButton mood="axiata-blue-outline" onClick={handleExpandAll}>
            Expand All
          </DefaultButton>
        )}
      </div>

      <Accordion
        type="multiple"
        className="w-full space-y-12"
        value={expanded}
        onValueChange={handleToggleAccordion}
      >
        <AccordionItem value="item-1">
          <AccordionTrigger>
            <div className="flex items-center gap-2">
              <H4>Preparation Data Integration to HPM</H4>
              {allFormsHaveData && <CheckSquare2 color="#5dee7a" />}
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-6 rounded py-6 bg-blend-soft-light dark:bg-neutral-800">
              <H4>Memo Active</H4>
              <MemoActiveForm
                isReadOnly={readOnly || disableFormValidator}
                onFormDataChange={setHasMemoData}
              />
            </div>

            <div className="space-y-6 rounded py-6 bg-blend-soft-light dark:bg-neutral-800">
              <H4>Create data GPON</H4>
              <CreateDataGPONForm
                isReadOnly={readOnly || disableFormValidator}
                onFormDataChange={setHasGPONData}
              />
            </div>

            <div className="space-y-6 rounded py-6 bg-blend-soft-light dark:bg-neutral-800">
              <H4>Create Homepass (SiteID)</H4>
              <CreateHomepassForm
                isReadOnly={readOnly || disableFormValidator}
                onFormDataChange={setHasHomepassData}
              />
            </div>

            <div className="space-y-6 rounded py-6 bg-blend-soft-light dark:bg-neutral-800">
              <H4>Create HPDB</H4>
              <DownloadHPDBForm
                isReadOnly={readOnly || disableFormValidator}
                onFormDataChange={setHasHPDBData}
              />
            </div>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="item-2">
          <AccordionTrigger>
            <div className="flex items-center gap-2">
              <H4>Processing Data to HPM</H4>
              {allSuccess && <CheckSquare2 color="#5dee7a" />}
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <CreateMemoHandler
              isReadOnly={readOnly || disableFormValidator}
              onStatusChange={setMemoStatus}
            />
            <CreateFibernodeHandler
              isReadOnly={isValidator && !canValidatorSubmit}
              onStatusChange={setFibernodeStatus}
            />
            <CreateDataGPONHandler
              isReadOnly={isValidator && !canValidatorSubmit}
              onStatusChange={setGponStatus}
            />
            <CreateHomepassHandler
              isReadOnly={isValidator && !canValidatorSubmit}
              onStatusChange={setHomepassStatus}
            />
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <div className="space-y-6 rounded py-6 bg-blend-soft-light dark:bg-neutral-800">
        <GenerateHPDBHandler />
      </div>

      <div className="space-y-6 rounded py-6 bg-blend-soft-light dark:bg-neutral-800">
        <GenerateMemoActiveHandler
          isReadOnly={readOnly || disableFormValidator}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <GenerateKMZHandler />
        <GeneratePDFHandler />
      </div>

      <div className="space-y-6 rounded py-6 bg-blend-soft-light dark:bg-neutral-800">
        <H4>Update RFS Submission</H4>
        <UpdateRFSSubmissionForm
          isReadOnly={!isAdmin && !(isValidator && canValidatorSubmit)}
        />
      </div>

      <FormFooter>
        <DefaultButton
          mood="axiata-reddish-outline"
          type="button"
          onClick={handleReject}
          disabled={!isAdmin && !(isValidator && canValidatorSubmit)}
        >
          Reject
        </DefaultButton>
        <SubmitButton
          onClick={handleFinish}
          disabled={!isAdmin && !(isValidator && canValidatorSubmit)}
        >
          Finish
        </SubmitButton>
      </FormFooter>

      <div className="hidden">
        <MemoActiveForm
          isReadOnly={readOnly || disableFormValidator}
          onFormDataChange={setHasMemoData}
        />
        <CreateDataGPONForm
          isReadOnly={readOnly || disableFormValidator}
          onFormDataChange={setHasGPONData}
        />
        <CreateHomepassForm
          isReadOnly={readOnly || disableFormValidator}
          onFormDataChange={setHasHomepassData}
        />
        <DownloadHPDBForm
          isReadOnly={readOnly || disableFormValidator}
          onFormDataChange={setHasHPDBData}
        />
        <CreateMemoHandler
          isReadOnly={readOnly || disableFormValidator}
          onStatusChange={setMemoStatus}
        />
        <CreateFibernodeHandler
          isReadOnly={disableFormValidator}
          onStatusChange={setFibernodeStatus}
        />
        <CreateDataGPONHandler
          isReadOnly={disableFormValidator}
          onStatusChange={setGponStatus}
        />
        <CreateHomepassHandler
          isReadOnly={disableFormValidator}
          onStatusChange={setHomepassStatus}
        />
      </div>
    </>
  );
}
